import { Button, List, message, Typography, Popconfirm, Tag, Alert } from 'antd'
import { MailOutlined } from '@ant-design/icons'
import Container from 'components/Container'
import { useLocalization } from 'localization'
import { useMemo } from 'react'
import {
	ICPA,
	IUserInvite,
	useAcceptInvite,
	useCPAs,
	useCurrentUser,
	useReceiverInvites,
	useRejectInvite,
	useRemoveCPA,
	useRemoveInvite,
	useSenderInvites,
	useSendInvite,
} from 'api'
import Skeleton from './skeleton'
import { Formik, FormikHelpers } from 'formik'
import { Form, FormItem, Input } from 'formik-antd'
import { createValidationSchema } from 'utils/validation'
import { getTagColorByInviteStatus } from './helpers'
import { InviteStatus } from 'api/users/dtos'
import { useScreenMatch } from 'theme/hooks'
import Styled from './styled'
import { useAppContext } from 'modules/common/appContext'
import { Trans } from 'react-i18next'

interface IFormValues {
	email: string
}

const schema = createValidationSchema(yup =>
	yup.object({
		email: yup.string().email().required(),
	})
)

const MAX_RETRIES = 5

type ListType = IUserInvite | ICPA

const AccessTab = () => {
	const { data: user } = useCurrentUser()
	const { data: senderInvites = [], isFetched: areSenderInvitesFetched } = useSenderInvites('ACCESS')
	const { data: receiverInvites = [], isFetched: areReceiverInvitesFetched } = useReceiverInvites('ACCESS')
	const { data: cpas = [], isFetched: areCPAsFetched } = useCPAs({ cacheTime: 0, staleTime: 0 }) // enable refetching when unmounted
	const { mutateAsync: sendInvite } = useSendInvite()
	const { mutateAsync: removeInvite } = useRemoveInvite()
	const { mutateAsync: removeCPA } = useRemoveCPA()
	const { mutateAsync: acceptInvite, isLoading: isAcceptingLoading } = useAcceptInvite()
	const { mutateAsync: rejectInvite, isLoading: isRejectLoading } = useRejectInvite()
	const { t } = useLocalization()
	const match = useScreenMatch()
	const { isFetched: isWealthicaFetched, isWealthicaContext } = useAppContext()
	const isFetched = areSenderInvitesFetched && areReceiverInvitesFetched && areCPAsFetched

	const cpasWithInvites = useMemo(
		() =>
			cpas
				.map(cpa => ({ ...cpa }))
				.concat(senderInvites as any)
				.concat(receiverInvites as any) as ListType[],
		[cpas, senderInvites, receiverInvites]
	)

	const initialValues: IFormValues = {
		email: '',
	}

	const onInvite = async (values: IFormValues, { resetForm }: FormikHelpers<IFormValues>) => {
		await sendInvite({ email: values.email, type: 'ACCESS' })
		resetForm()
	}

	return (
		<Container display="flex" flexDirection="column" space="sm" spaceDirection="vertical">
			{isWealthicaFetched && !isWealthicaContext && (
				<Typography.Text>
					{cpasWithInvites.some(item => !('retries' in item))
						? t('settings.access.descriptionAccepted')
						: t('settings.access.description')}
				</Typography.Text>
			)}
			{!isFetched || isAcceptingLoading || !isWealthicaFetched ? (
				<Skeleton />
			) : (
				<>
					{isWealthicaContext && (
						<Typography.Text>
							<Trans key="settings.access.wealthicaNoAccess">
								Sharing access to Tax Specialist is only supported in the full BlockSentry app outside the Add-On. If
								you are interested in this feature, please send email to{' '}
								<Typography.Link href="mailto:contact@blocksentry.co">contact@blocksentry.co</Typography.Link>.
							</Trans>
						</Typography.Text>
					)}
					{cpasWithInvites.length > 0 && !isWealthicaContext && (
						<List
							size="small"
							dataSource={cpasWithInvites}
							renderItem={item => (
								<Container margin={{ bottom: 'xs' }}>
									<Styled.InviteCard size="small">
										{'retries' in item && item.email !== user?.email && (
											<>
												<Typography.Text>{item.email}</Typography.Text>
												<Container
													margin={{ left: 'auto' }}
													display="flex"
													alignItems="center"
													justifyContent="flex-end"
												>
													<Tag color={getTagColorByInviteStatus(item.status)}>
														{t(`settings.access.inviteStatuses.${item.status}`)}
													</Tag>
													{item.status !== InviteStatus.APPROVED && (
														<Button
															size="small"
															disabled={item.resentRecently}
															type="link"
															onClick={() => {
																if (item.retries >= MAX_RETRIES) {
																	message.error(t('settings.access.retriesExceeded'))
																	return
																}
																sendInvite({ email: item.email, shouldRefetch: false, type: 'ACCESS' })
															}}
														>
															{item.resentRecently ? t('terms.resent') : t('actions.resend')}
														</Button>
													)}
													<Popconfirm
														title={t('settings.access.removeInvite')}
														onConfirm={() => removeInvite(item.id)}
														okText={t('actions.confirm')}
														cancelText={t('actions.cancel')}
														okButtonProps={{ danger: true }}
														icon={null}
													>
														<Button size="small" danger type="link">
															{t('actions.delete')}
														</Button>
													</Popconfirm>
												</Container>
											</>
										)}
										{'retries' in item && item.email === user?.email && (
											<>
												<Typography.Text>{item.sender.email}</Typography.Text>
												<Container
													margin={{ left: 'auto' }}
													display="flex"
													alignItems="center"
													justifyContent="flex-end"
												>
													<Tag color={getTagColorByInviteStatus(item.status)}>
														{t(`settings.access.inviteStatuses.${item.status}`)}
													</Tag>
													{item.status !== InviteStatus.APPROVED && (
														<Button
															size="small"
															type="link"
															loading={isAcceptingLoading}
															onClick={() => {
																acceptInvite(item.id)
															}}
														>
															{t('actions.acceptInvite')}
														</Button>
													)}
													<Popconfirm
														title={t('clients.rejectModal.subtitle', { email: item.sender.email })}
														onConfirm={() => rejectInvite(item.id)}
														okText={t('actions.confirm')}
														cancelText={t('actions.cancel')}
														okButtonProps={{ danger: true, loading: isRejectLoading }}
														icon={null}
													>
														<Button size="small" danger type="link">
															{t('actions.reject')}
														</Button>
													</Popconfirm>
												</Container>
											</>
										)}
										{!('retries' in item) && (
											<>
												<Typography.Text>{item.email}</Typography.Text>
												<Container
													margin={{ left: 'auto' }}
													display="flex"
													alignItems="center"
													justifyContent="flex-end"
												>
													<Popconfirm
														title={t('settings.access.revokeAccess', { email: item.email })}
														onConfirm={() => removeCPA(item.id)}
														okText={t('actions.confirm')}
														cancelText={t('actions.cancel')}
														okButtonProps={{ danger: true }}
														icon={null}
													>
														<Button danger type="link" size="small">
															{t('actions.revokeAccess')}
														</Button>
													</Popconfirm>
												</Container>
											</>
										)}
									</Styled.InviteCard>
								</Container>
							)}
							locale={{
								emptyText: <></>,
							}}
						/>
					)}
					{/* allow only one invite and cpa */}
					{cpas.length === 0 && !isWealthicaContext && (
						<Container space="sm" spaceDirection="vertical">
							{cpasWithInvites.length > 0 && <Alert showIcon type="warning" message={t('settings.access.warning')} />}
							<Formik enableReinitialize onSubmit={onInvite} validationSchema={schema} initialValues={initialValues}>
								{({ isValid, dirty, isSubmitting }) => (
									<Form layout="vertical">
										<Container
											display="flex"
											spaceDirection={match.gte('sm') ? 'horizontal' : 'vertical'}
											flexDirection={match.gte('sm') ? 'row' : 'column'}
											justifyContent="space-between"
											space="md"
										>
											<FormItem name="email" style={{ width: '100%' }}>
												<Input name="email" placeholder={t('placeholders.email')} />
											</FormItem>
											<Button
												type="primary"
												htmlType="submit"
												icon={<MailOutlined />}
												loading={isSubmitting}
												disabled={!isValid || !dirty}
											>
												{t('actions.invite')}
											</Button>
										</Container>
									</Form>
								)}
							</Formik>
						</Container>
					)}
				</>
			)}
		</Container>
	)
}

export default AccessTab
